<template>
  <div :class="wrapClasses" @mouseleave="$_handleMouseLeave($event)">
    <div ref="hiddenColumns" class="hidden-columns"><slot></slot></div>
    <!--header-->
    <div
      v-if="showHeader"
      ref="headerWrapper"
      v-mousewheel="$_handleHeaderFooterMousewheel"
      :class="prefixClass+'__header-wrapper'"
    >
      <table-header
        ref="tableHeader"
        :store="store"
        :border="border"
        :default-sort="defaultSort"
        :style="{
          width: layout.bodyWidth ? layout.bodyWidth + 'px' : ''
        }"
      >
      </table-header>
    </div>
    <!--body-->
    <div
      ref="bodyWrapper"
      :class="[prefixClass+'__body-wrapper',[layout.scrollX ? `is-scrolling-${scrollPosition}` : 'is-scrolling-none']]"
      :style="[bodyHeight]"
    >
      <table-body
        :context="context"
        :store="store"
        :stripe="stripe"
        :row-class-name="rowClassName"
        :row-style="rowStyle"
        :highlight="highlightCurrentRow"
        :style="{
          width: bodyWidth
        }"
      >
      </table-body>
      <div
        v-if="hasTips"
        ref="emptyBlock"
        :class="prefixClass+'__empty-block'"
      >
        <div v-if="loading" v-html="localeLoadingDataText"></div>
        <span v-else :class="prefixClass+'__empty-text'">
          <slot name="empty">
            <t-empty :desc="localeNoDataText"></t-empty>
            <!-- <span v-if="noData" v-html="localeNoDataText"></span> -->
          </slot>
        </span>
      </div>
      <div
        v-if="$slots.append"
        ref="appendWrapper"
        :class="prefixClass+'__append-wrapper'"
      >
        <slot name="append"></slot>
      </div>
    </div>
    <!--footer-->
    <div
      v-if="showSummary"
      v-show="data && data.length > 0"
      ref="footerWrapper"
      v-mousewheel="$_handleHeaderFooterMousewheel"
      :class="prefixClass+'__footer-wrapper'"
    >
      <table-footer
        :store="store"
        :border="border"
        :sum-text="localeSumText"
        :summary-method="summaryMethod"
        :default-sort="defaultSort"
        :style="{
          width: layout.bodyWidth ? layout.bodyWidth + 'px' : ''
        }"
      >
      </table-footer>
    </div>
    <!--fixed column-->
    <div
      v-if="fixedColumns.length > 0"
      ref="fixedWrapper"
      v-mousewheel="$_handleFixedMousewheel"
      :class="prefixClass+'__fixed'"
      :style="[{
                 width: layout.fixedWidth ? layout.fixedWidth + 'px' : ''
               },
               fixedHeight]"
    >
      <div
        v-if="showHeader"
        ref="fixedHeaderWrapper"
        :class="prefixClass+'__fixed-header-wrapper'"
      >
        <table-header
          ref="fixedTableHeader"
          fixed="left"
          :border="border"
          :store="store"
          :style="{
            width: bodyWidth
          }"
        ></table-header>
      </div>
      <div
        ref="fixedBodyWrapper"
        class="table__fixed-body-wrapper"
        :style="[{
                   top: layout.headerHeight + 'px'
                 },
                 fixedBodyHeight]"
      >
        <table-body
          fixed="left"
          :store="store"
          :stripe="stripe"
          :highlight="highlightCurrentRow"
          :row-class-name="rowClassName"
          :row-style="rowStyle"
          :style="{
            width: bodyWidth
          }"
        >
        </table-body>
        <div
          v-if="$slots.append"
          class="table__append-gutter"
          :style="{
            height: layout.appendHeight + 'px'
          }"
        ></div>
      </div>
      <div
        v-if="showSummary"
        v-show="data && data.length > 0"
        ref="fixedFooterWrapper"
        class="table__fixed-footer-wrapper"
      >
        <table-footer
          fixed="left"
          :border="border"
          :sum-text="sumText || t('el.table.sumText')"
          :summary-method="summaryMethod"
          :store="store"
          :style="{
            width: bodyWidth
          }"
        ></table-footer>
      </div>
    </div>
    <!--right fixed column-->
    <div
      v-if="rightFixedColumns.length > 0"
      ref="rightFixedWrapper"
      v-mousewheel="$_handleFixedMousewheel"
      :class="prefixClass+'__fixed-right'"
      :style="[{
                 width: layout.rightFixedWidth ? layout.rightFixedWidth + 'px' : '',
                 right: layout.scrollY ? (border ? layout.gutterWidth : (layout.gutterWidth || 0)) + 'px' : ''
               },
               fixedHeight]"
    >
      <div v-if="showHeader"
           ref="rightFixedHeaderWrapper"
           :class="prefixClass+'__fixed-header-wrapper'"
      >
        <table-header
          ref="rightFixedTableHeader"
          fixed="right"
          :border="border"
          :store="store"
          :style="{
            width: bodyWidth
          }"
        ></table-header>
      </div>
      <div
        ref="rightFixedBodyWrapper"
        :class="prefixClass+'__fixed-body-wrapper'"
        :style="[{
                   top: layout.headerHeight + 'px'
                 },
                 fixedBodyHeight]"
      >
        <table-body
          fixed="right"
          :store="store"
          :stripe="stripe"
          :row-class-name="rowClassName"
          :row-style="rowStyle"
          :highlight="highlightCurrentRow"
          :style="{
            width: bodyWidth
          }"
        >
        </table-body>
      </div>
      <div
        v-if="showSummary"
        v-show="data && data.length > 0"
        ref="rightFixedFooterWrapper"
        :class="prefixClass+'__fixed-footer-wrapper'"
      >
        <table-footer
          fixed="right"
          :border="border"
          :sum-text="sumText || t('el.table.sumText')"
          :summary-method="summaryMethod"
          :store="store"
          :style="{
            width: bodyWidth
          }"
        ></table-footer>
      </div>
    </div>
    <div
      v-if="rightFixedColumns.length > 0"
      ref="rightFixedPatch"
      :class="prefixClass+'__fixed-right-patch'"
      :style="{
        width: layout.scrollY ? layout.gutterWidth + 'px' : '0',
        height: layout.headerHeight + 'px'
      }"
    ></div>
    <div v-show="resizeProxyVisible" ref="resizeProxy" :class="prefixClass+'__column-resize-proxy'"></div>
  </div>
</template>

<script>
import { PREFIX_CLASS } from '../../utils/constants'
import Locale from '../../mixins/locale'
import Migrating from '@/mixins/migrating'
import { debounce } from 'throttle-debounce'
import { addResizeListener, removeResizeListener } from '@/utils/resize-event'
import Mousewheel from '@/directives/mousewheel'
import TableStore from './table-store'
import TableLayout from './table-layout'
import TableBody from './table-body.vue'
import TableHeader from './table-header'
import TableFooter from './table-footer'
import { getRowIdentity } from './util'
const PREFIX_TABLE_CLASS = `${PREFIX_CLASS}table`
const flattenData = function(data) {
  if (!data) return data
  let newData = []
  const flatten = arr => {
    arr.forEach((item) => {
      newData.push(item)
      if (Array.isArray(item.children)) {
        flatten(item.children)
      }
    })
  }
  flatten(data)
  if (data.length === newData.length) {
    return data
  } else {
    return newData
  }
}
let tableIdSeed = 1

export default {
  name: 'TTable',
  directives: {
    Mousewheel
  },
  components: {
    TableHeader,
    TableFooter,
    TableBody
    // TCheckbox
  },
  mixins: [Locale, Migrating],
  props: {
    data: {
      type: Array,
      default: function() {
        return []
      }
    },
    size: String,
    width: [String, Number],

    height: [String, Number],

    maxHeight: [String, Number],
    stripe: Boolean,
    border: Boolean,
    line: Boolean,
    fit: {
      type: Boolean,
      default: true
    },
    rowKey: [String, Function],

    context: {},

    showHeader: {
      type: Boolean,
      default: true
    },

    showSummary: Boolean,

    sumText: String,

    summaryMethod: Function,

    rowClassName: [String, Function],

    rowStyle: [Object, Function],

    cellClassName: [String, Function],

    cellStyle: [Object, Function],

    headerRowClassName: [String, Function],

    headerRowStyle: [Object, Function],

    headerCellClassName: [String, Function],

    headerCellStyle: [Object, Function],

    highlightCurrentRow: Boolean,

    currentRowKey: [String, Number],

    emptyText: String,

    expandRowKeys: Array,

    defaultExpandAll: Boolean,

    reserveSelection: Boolean,

    defaultSort: Object,

    spanMethod: Function,

    selectionList: {
      type: Array,
      default: () => []
    },

    selectOnIndeterminate: {
      type: Boolean,
      default: true
    },

    indent: {
      type: Number,
      default: 16
    },

    lazy: Boolean,

    load: Function,

    loading: Boolean,
    allShowOverflowTooltip: Boolean,
    allShowTooltipWhenOverflow: Boolean
  },
  data() {
    const store = new TableStore(this, {
      rowKey: this.rowKey,
      reserveSelection: this.reserveSelection,
      defaultExpandAll: this.defaultExpandAll,
      selectOnIndeterminate: this.selectOnIndeterminate,
      indent: this.indent,
      lazy: this.lazy,
      allShowOverflowTooltip: this.allShowOverflowTooltip,
      allShowTooltipWhenOverflow: this.allShowTooltipWhenOverflow,
      selectionList: this.selectionList,
      selection: this.selectionList
    })
    const layout = new TableLayout({
      store,
      table: this,
      fit: this.fit,
      showHeader: this.showHeader
    })
    return {
      prefixClass: PREFIX_TABLE_CLASS,
      layout,
      store,
      isHidden: false,
      renderExpanded: null,
      resizeProxyVisible: false,
      resizeState: {
        width: null,
        height: null
      },
      // 是否拥有多级表头
      isGroup: false,
      scrollPosition: 'left'
    }
  },
  computed: {
    localeNoDataText() {
      // 没有数据的文本
      if (this.noDataText === undefined) {
        return this.t('t.table.noDataText')
      } else {
        return this.noDataText
      }
    },
    localeSumText() {
      // 合计的文本
      if (this.sumText === undefined) {
        return this.t('t.table.sumText')
      } else {
        return this.sumText
      }
    },
    localeNoFilteredDataText() {
      // 本地过滤没有数据的提示文本
      if (this.noFilteredDataText === undefined) {
        return this.t('t.table.noFilteredDataText')
      } else {
        return this.noFilteredDataText
      }
    },
    localeLoadingDataText() {
      // 加载中提示文本
      if (this.loading) {
        return `${this.t('t.table.loading')}`
      }
      return ''
    },
    wrapClasses() {
      return [
        `${PREFIX_TABLE_CLASS}`,
        this.size ? `${PREFIX_TABLE_CLASS}--${this.size}` : '',
        {
          [`${PREFIX_TABLE_CLASS}--fit`]: !this.fit,
          [`${PREFIX_TABLE_CLASS}--striped`]: this.stripe,
          [`${PREFIX_TABLE_CLASS}--border`]: this.border || this.isGroup,
          [`${PREFIX_TABLE_CLASS}--line`]: this.line,
          [`${PREFIX_TABLE_CLASS}--hidden`]: this.isHidden,
          [`${PREFIX_TABLE_CLASS}--group`]: this.isGroup,
          [`${PREFIX_TABLE_CLASS}--fluid-height`]: this.maxHeight,
          [`${PREFIX_TABLE_CLASS}--scrollable-x`]: this.layout.scrollX,
          [`${PREFIX_TABLE_CLASS}--scrollable-y`]: this.layout.scrollY,
          [`${PREFIX_TABLE_CLASS}--enable-row-hover`]: !this.store.states.isComplex,
          [`${PREFIX_TABLE_CLASS}--enable-row-transition`]: (this.store.states.data || []).length !== 0 && (this.store.states.data || []).length < 100
        }
      ]
    },
    bodyWrapper() {
      return this.$refs.bodyWrapper
    },
    shouldUpdateHeight() {
      return this.height ||
        this.maxHeight ||
        this.fixedColumns.length > 0 ||
        this.rightFixedColumns.length > 0
    },

    selection() {
      return this.store.states.selection
    },

    columns() {
      return this.store.states.columns
    },

    tableData() {
      return this.store.states.data
    },

    fixedColumns() {
      return this.store.states.fixedColumns
    },

    rightFixedColumns() {
      return this.store.states.rightFixedColumns
    },
    fixed() {
      return !!(this.height || this.fixedColumns.length || this.rightFixedColumns.length)
    },
    bodyWidth() {
      const { bodyWidth, scrollY, gutterWidth } = this.layout
      return bodyWidth ? bodyWidth - (scrollY ? gutterWidth : 0) + 'px' : ''
    },

    bodyHeight() {
      if (this.height) {
        return {
          height: this.layout.bodyHeight ? this.layout.bodyHeight + 'px' : ''
        }
      } else if (this.maxHeight) {
        return {
          'max-height': (this.showHeader
            ? this.maxHeight - this.layout.headerHeight - this.layout.footerHeight
            : this.maxHeight - this.layout.footerHeight) + 'px'
        }
      }
      return {}
    },

    fixedBodyHeight() {
      if (this.height) {
        return {
          height: this.layout.fixedBodyHeight ? this.layout.fixedBodyHeight + 'px' : ''
        }
      } else if (this.maxHeight) {
        let maxHeight = this.layout.scrollX ? this.maxHeight - this.layout.gutterWidth : this.maxHeight

        if (this.showHeader) {
          maxHeight -= this.layout.headerHeight
        }

        maxHeight -= this.layout.footerHeight

        return {
          'max-height': maxHeight + 'px'
        }
      }

      return {}
    },

    fixedHeight() {
      if (this.maxHeight) {
        if (this.showSummary) {
          return {
            bottom: 0
          }
        }
        return {
          bottom: (this.layout.scrollX && this.data.length) ? this.layout.gutterWidth + 'px' : ''
        }
      } else {
        if (this.showSummary) {
          return {
            height: this.layout.tableHeight ? this.layout.tableHeight + 'px' : ''
          }
        }
        return {
          height: this.layout.viewportHeight ? this.layout.viewportHeight + 'px' : ''
        }
      }
    },
    noData() {
      return !this.data || !this.data.length
    },
    hasTips() {
      // 在loading状态 无数据 无过滤数据 三种状态下展示提示
      return this.loading || this.noData
    }
  },
  watch: {
    height: {
      immediate: true,
      handler(value) {
        this.layout.setHeight(value)
      }
    },

    maxHeight: {
      immediate: true,
      handler(value) {
        this.layout.setMaxHeight(value)
      }
    },

    currentRowKey(newVal) {
      this.store.setCurrentRowKey(newVal)
    },

    data: {
      immediate: true,
      handler(value) {
        this.store.states.treeData = this.$_getTableTreeData(value)
        value = flattenData(value)
        this.store.commit('setData', value)
        if (this.$ready) {
          this.$nextTick(() => {
            this.$_doLayout()
          })
        }
      }
    },

    expandRowKeys: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.store.setExpandRowKeys(newVal)
        }
      }
    }
  },

  destroyed() {
    if (this.resizeListener) removeResizeListener(this.$el, this.resizeListener)
  },
  created() {
    this.tableId = this.prefixClass + tableIdSeed++
    this.debouncedUpdateLayout = debounce(50, () => this.$_doLayout())
  },
  mounted() {
    this.$_bindEvents()
    this.store.updateColumns()
    this.$_doLayout()

    this.resizeState = {
      width: this.$el.offsetWidth,
      height: this.$el.offsetHeight
    }

    // init filters
    this.store.states.columns.forEach(column => {
      if (column.filteredValue && column.filteredValue.length) {
        this.store.commit('filterChange', {
          column,
          values: column.filteredValue,
          silent: true
        })
      }
    })

    this.$ready = true
  },
  methods: {
    $_handleMouseLeave() {
      this.store.commit('setHoverRow', null)
      if (this.hoverState) this.hoverState = null
    },
    $_handleHeaderFooterMousewheel(event, data) {
      const { pixelX, pixelY } = data
      if (Math.abs(pixelX) >= Math.abs(pixelY)) {
        event.preventDefault()
        this.bodyWrapper.scrollLeft += data.pixelX / 5
      }
    },
    $_handleFixedMousewheel(event, data) {
      const bodyWrapper = this.bodyWrapper
      if (Math.abs(data.spinY) > 0) {
        const currentScrollTop = bodyWrapper.scrollTop
        if (data.pixelY < 0 && currentScrollTop !== 0) {
          event.preventDefault()
        }
        if (data.pixelY > 0 && bodyWrapper.scrollHeight - bodyWrapper.clientHeight > currentScrollTop) {
          event.preventDefault()
        }
        bodyWrapper.scrollTop += Math.ceil(data.pixelY / 5)
      } else {
        bodyWrapper.scrollLeft += Math.ceil(data.pixelX / 5)
      }
    },
    $_getMigratingConfig() {
      return {
        events: {
          expand: 'expand is renamed to expand-change'
        }
      }
    },
    setCurrentRow(row) {
      this.store.commit('setCurrentRow', row)
    },
    toggleRowSelection(row, selected) {
      this.store.toggleRowSelection(row, selected)
      this.store.updateAllSelected()
    },

    $_toggleRowExpansion(row, expanded) {
      this.store.toggleRowExpansion(row, expanded)
    },

    clearSelection() {
      this.store.clearSelection()
    },

    clearFilter(columnKeys) {
      this.store.clearFilter(columnKeys)
    },

    $_clearSort() {
      this.store.clearSort()
    },
    $_updateScrollY() {
      this.layout.updateScrollY()
      this.layout.updateColumnsWidth()
    },
    $_bindEvents() {
      const { headerWrapper, footerWrapper } = this.$refs
      const refs = this.$refs
      let self = this

      this.bodyWrapper.addEventListener('scroll', function() {
        if (headerWrapper) headerWrapper.scrollLeft = this.scrollLeft
        if (footerWrapper) footerWrapper.scrollLeft = this.scrollLeft
        if (refs.fixedBodyWrapper) refs.fixedBodyWrapper.scrollTop = this.scrollTop
        if (refs.rightFixedBodyWrapper) refs.rightFixedBodyWrapper.scrollTop = this.scrollTop
        const maxScrollLeftPosition = this.scrollWidth - this.offsetWidth - 1
        const scrollLeft = this.scrollLeft
        if (scrollLeft >= maxScrollLeftPosition) {
          self.scrollPosition = 'right'
        } else if (scrollLeft === 0) {
          self.scrollPosition = 'left'
        } else {
          self.scrollPosition = 'middle'
        }
      })

      if (this.fit) {
        addResizeListener(this.$el, this.resizeListener)
      }
    },

    $_resizeListener() {
      if (!this.$ready) return
      let shouldUpdateLayout = false
      const el = this.$el
      const { width: oldWidth, height: oldHeight } = this.resizeState

      const width = el.offsetWidth
      if (oldWidth !== width) {
        shouldUpdateLayout = true
      }

      const height = el.offsetHeight
      if ((this.height || this.shouldUpdateHeight) && oldHeight !== height) {
        shouldUpdateLayout = true
      }

      if (shouldUpdateLayout) {
        this.resizeState.width = width
        this.resizeState.height = height
        this.$_doLayout()
      }
    },

    $_doLayout() {
      this.layout.updateColumnsWidth()
      if (this.shouldUpdateHeight) {
        this.layout.updateElsHeight()
      }
    },

    $_sort(prop, order) {
      this.store.commit('sort', { prop, order })
    },

    $_toggleAllSelection() {
      this.store.commit('toggleAllSelection')
    },

    getRowKey(row) {
      const rowKey = getRowIdentity(row, this.store.states.rowKey)
      if (!rowKey) {
        throw new Error('if there\'s nested data, rowKey is required.')
      }
      return rowKey
    },

    $_getTableTreeData(data) {
      const treeData = {}
      const traverse = (children, parentData, level) => {
        children.forEach(item => {
          const rowKey = this.getRowKey(item)
          treeData[rowKey] = {
            display: false,
            level
          }
          parentData.children.push(rowKey)
          if (Array.isArray(item.children) && item.children.length) {
            treeData[rowKey].children = []
            treeData[rowKey].expanded = false
            traverse(item.children, treeData[rowKey], level + 1)
          }
        })
      }
      if (data) {
        data.forEach(item => {
          const containChildren = Array.isArray(item.children) && item.children.length
          if (!(containChildren || item.hasChildren)) return
          const rowKey = this.getRowKey(item)
          const treeNode = {
            level: 0,
            expanded: false,
            display: true,
            children: []
          }
          if (containChildren) {
            treeData[rowKey] = treeNode
            traverse(item.children, treeData[rowKey], 1)
          } else if (item.hasChildren && this.lazy) {
            treeNode.hasChildren = true
            treeNode.loaded = false
            treeData[rowKey] = treeNode
          }
        })
      }
      return treeData
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/module/table/table.vue